import { InvoicingSystem } from "./settings/invoicingSettings"

export type PaymentMethodKind = 'cash' | 'check' | 'card' | 'ach'

export type CollectedSurcharge = {
  collectedSurcharge: number
  collectedSurchargeLabel: string | null
  amountPaid: number
}

export const defaultCollectedSurcharge = (): CollectedSurcharge => ({
  collectedSurcharge: 0,
  collectedSurchargeLabel: null,
  amountPaid: 0
})

export type InvoiceSummary = {
  invoiceId: string
  jobId: number
  tenantId: number
  transactionId: string
  customerId: number
  statementId: string | null
  title: string
  invoiceNumber: string
  createdAt: string
  dueOn?: string
  qboInvoiceId: string
  qboRealmId: string
  qboDisplayName: string
  amount: number
  salesTax: number
  total: number
  balance: number
  wasEmailSent: boolean
  emailSentAt: string | null
  qboInvoiceExists: boolean
  qboEmailStatus: QuickBooksEmailStatus
  qboAmountWithoutTax: number
  qboSalesTax: number
  qboBalance: number
  lastEventId: number
  lastUpdatedAt: string
  invoicingSystem: InvoicingSystem
  hasManualPayment: boolean
  hasPaymentProblem: boolean
  hasUnexportedPayment: boolean
  hasPendingPayment: boolean
  paymentMethod: PaymentMethodKind | null
  checkNumber: string | null
  lastFour: string | null
} & CollectedSurcharge

export type QuickBooksEmailStatus = 'NotSet' | 'NeedToSend' | 'Sent'

export const defaultInvoiceSummary = (): InvoiceSummary => ({
  invoiceId: '',
  jobId: 0,
  tenantId: 0,
  transactionId: '',
  customerId: 0,
  statementId: null,
  title: '',
  invoiceNumber: '',
  createdAt: '',
  qboInvoiceId: '',
  qboRealmId: '',
  qboDisplayName: '',
  amount: 0,
  salesTax: 0,
  total: 0,
  balance: 0,
  wasEmailSent: false,
  emailSentAt: null,
  qboInvoiceExists: false,
  qboEmailStatus: 'NotSet',
  qboAmountWithoutTax: 0,
  qboSalesTax: 0,
  qboBalance: 0,
  lastEventId: 0,
  lastUpdatedAt: '',
  invoicingSystem: 'quickBooks',
  hasManualPayment: false,
  hasPaymentProblem: false,
  hasUnexportedPayment: false,
  hasPendingPayment: false,
  paymentMethod: null,
  checkNumber: null,
  lastFour: null,
  ...defaultCollectedSurcharge()
})

export type InvoiceDetail = {
  invoice: Invoice
  quickBooksInvoice?: QuickBooksInvoice | null
  qboInvoiceUrl: string
  paymentPageUrl: string
}

export type Invoice = {
  id: string
  jobId: number
  qboInvoiceId: string
  lineItems: InvoiceLineItem[]
  amount: number
  salesTax: number
  total: number
  dueOn: string
  enableCreditCardPayments: boolean
  enableAchPayments: boolean
  showAuthorizePaymentOnFile: boolean
  requireAuthorizePaymentOnFile: boolean
  invoicingSystem: InvoicingSystem
  customerId: number
}

export type InvoiceLineItem = {
  description: string
  descriptionHtml: string | null
  type: 'charge' | 'salesTaxCharge'
  amount: number
}

export const defaultInvoice = (): Invoice => ({
  id: '',
  jobId: 0,
  qboInvoiceId: '',
  lineItems: [],
  amount: 0,
  salesTax: 0,
  total: 0,
  dueOn: '',
  enableCreditCardPayments: false,
  enableAchPayments: false,
  showAuthorizePaymentOnFile: false,
  requireAuthorizePaymentOnFile: false,
  invoicingSystem: 'quickBooks',
  customerId: 0
})

export const defaultInvoiceLineItem = (): InvoiceLineItem => ({
  description: '',
  descriptionHtml: null,
  type: 'charge',
  amount: 0
})

export type QuickBooksInvoice = {
  amountWithoutTax: number
  totalAmount: number
  totalSalesTax: number
  balance: number
  emailAddress: string
  lastUpdatedAt: string
  lineItems: QuickBooksLineItem[]
}

export type QuickBooksLineItem = {
  type: 'salesItemLineDetail' | 'subTotalLineDetail' | 'discountLineDetail' | 'groupLineDetail' | 'descriptionOnly'
  description: string | null | undefined
  amountWithoutTax: number | null | undefined
}

export const defaultQuickBooksInvoice = (): QuickBooksInvoice => ({
  amountWithoutTax: 0,
  totalAmount: 0,
  totalSalesTax: 0,
  balance: 0,
  emailAddress: '',
  lastUpdatedAt: '',
  lineItems: []
})

export const defaultQuickBooksLineItem = (): QuickBooksLineItem => ({
  type: 'salesItemLineDetail',
  description: '',
  amountWithoutTax: 0
})

export type InvoiceableWorkarea = { id: number; name: string }

export type JobForInvoicing = {
  invoiceableWorkAreas?: InvoiceableWorkarea[]
  id: number
  name: string
  type: number
  customer: {
    id: number
    displayName: string
  }
  visitsCount: number
  lastVisitDate: string
  status: number | null
  isNotReviewed: boolean
  isReviewed: boolean
  isUninvoiced: boolean
  hasCompletedVisits: boolean
  isInvoiced: boolean
  isPaid: boolean
  isOutstanding: boolean
  isNotInvoiced: boolean
  divisionGroups: string[]
  taskStatuses: number[]
  hasQuotedWorkReadyToInvoice: boolean
  quotedRemainingAmount: number
  quotedCompletedDate: string
  hasIntervalPaymentDue: boolean
  canBatchInvoice: boolean
}

export const defaultJobForInvoicing = (): JobForInvoicing => ({
  id: 0,
  name: '',
  type: 0,
  customer: { id: 0, displayName: ''},
  visitsCount: 0,
  lastVisitDate: '',
  status: null,
  isNotReviewed: false,
  isReviewed: false,
  isUninvoiced: false,
  hasCompletedVisits: false,
  isInvoiced: false,
  isPaid: false,
  isOutstanding: false,
  isNotInvoiced: false,
  divisionGroups: [],
  taskStatuses: [],
  hasQuotedWorkReadyToInvoice: false,
  quotedRemainingAmount: 0,
  quotedCompletedDate: '',
  hasIntervalPaymentDue: false,
  canBatchInvoice: false
})

export type ExportInvoiceData = {
  invoiceId: string
  dueOn: string
  enableCreditCardPayments: boolean
  enableAchPayments: boolean
}

export const canInvoiceBeMarkedPaidManually = (summary: InvoiceSummary) => {
  return summary.invoicingSystem === 'synkedUp' && summary.balance > 0
}

export const canInvoiceBeMarkedNotPaidManually = (summary: InvoiceSummary) => {
  return summary.invoicingSystem === 'synkedUp' && summary.balance === 0 && summary.hasManualPayment && !summary.statementId
}

export const wasInvoiceSent = (summary: InvoiceSummary): boolean => {
  return summary.invoicingSystem === 'synkedUp' ? summary.wasEmailSent : summary.qboEmailStatus === 'Sent'
}

export const isInvoiceBulkSelectable = (summary: InvoiceSummary): boolean => {
  return summary.invoicingSystem === 'synkedUp'
}

export const isInvoiceIndependentlyPayable = (summary: InvoiceSummary, parentStatement?: { wasEmailSent: boolean }): boolean => {
  if (!summary.statementId) {
    return true
  }
  // A child of a statement is individually payable only while the statement is unsent —
  // paying it detaches it. Once the statement is sent it's locked (pay via the statement).
  return parentStatement !== undefined && !parentStatement.wasEmailSent
}

export type InvoiceSummaryWithCustomerJobData = InvoiceSummary & {
  customerDisplayName: string
  customerQboId: string | null
  jobName: string
  jobNumber: string
}

export const getPaymentMethodDisplay = (summary: InvoiceSummary): string => {
  if (summary.paymentMethod === 'cash') {
    return 'Cash'
  }
  if (summary.paymentMethod === 'check') {
    return summary.checkNumber ? `Check #${summary.checkNumber}` : 'Check'
  }
  if (summary.paymentMethod === 'card') {
    return summary.lastFour ? `Card ending in ${summary.lastFour}` : 'Card'
  }
  if (summary.paymentMethod === 'ach') {
    return 'ACH'
  }
  return ''
}
